import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScroll } from 'react-use';
import useLocation from 'react-use/lib/useLocation';

import { ScrollCollapseContext } from '~/components/ScrollCollapse/contexts/ScrollCollapseContext';
import type {
  EasingFunction,
  TransitionDuration,
} from '~/components/ScrollCollapse/debug/ScrollCollapseControls';
import { interpolate } from '~/components/ScrollCollapse/utils/interpolate';

interface ScrollCollapseProviderProps {
  children: ReactNode;
  // A reference to the scrollable element, typically a container with overflow.
  scrollRef: RefObject<HTMLElement>;
  // The scroll distance (in pixels) from 0px to 100% to calculate scroll progress.
  scrollDistanceMax?: number;
  // The Tailwind CSS easing function to apply to the transition effect.
  easingFunction?: EasingFunction;
  // The duration of the transition effect (in milliseconds).
  transitionDuration?: TransitionDuration;
}

export function ScrollCollapseProvider({
  children,
  scrollRef,
  scrollDistanceMax: scrollDistanceMaxInitial = 100,
  easingFunction: easingFunctionInitial = 'ease',
  transitionDuration: transitionDurationInitial = 'duration-500',
}: ScrollCollapseProviderProps) {
  const location = useLocation();
  const { y: scrollDistance } = useScroll(scrollRef);
  // @TODO WMG1-573: Remove these values once the scroll collapse behavior is finalized.
  const [scrollDistanceMax, setScrollDistanceMax] = useState(
    scrollDistanceMaxInitial,
  );
  const [easingFunction, setEasingFunction] = useState<EasingFunction>(
    easingFunctionInitial,
  );
  const [transitionDuration, setTransitionDuration] =
    useState<TransitionDuration>(transitionDurationInitial);

  const scrollProgress = scrollDistance / scrollDistanceMax || 0;
  const headerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const getInterpolatedValue = useCallback(
    (max: number, min: number) => interpolate(max, min, scrollProgress),
    [scrollProgress],
  );

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current instanceof HTMLElement) {
      scrollRef.current?.style.setProperty(
        '--sticky-header-height',
        `${headerRef.current.offsetHeight || 0}px`,
      );
    }
  }, [headerRef, scrollRef]);

  // Observe the header element and invoke updateHeaderHeight when it resizes.
  const observeHeaderResize = useCallback(() => {
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(updateHeaderHeight);
    }

    if (headerRef.current instanceof HTMLElement) {
      resizeObserverRef.current.observe(headerRef.current);
    }
  }, [headerRef, updateHeaderHeight]);

  useEffect(() => {
    updateHeaderHeight();
    observeHeaderResize();

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [
    scrollDistance,
    headerRef,
    updateHeaderHeight,
    observeHeaderResize,
    location,
  ]);

  const valueMemoized = useMemo(() => {
    return {
      scrollProgress,
      getInterpolatedValue,
      headerRef,
      // @TODO WMG1-573: Remove these values once the scroll collapse behavior is finalized.
      scrollDistance,
      scrollDistanceMax,
      setScrollDistanceMax,
      easingFunction,
      setEasingFunction,
      transitionDuration,
      setTransitionDuration,
    };
  }, [
    easingFunction,
    getInterpolatedValue,
    scrollDistance,
    scrollDistanceMax,
    scrollProgress,
    transitionDuration,
  ]);

  return (
    <ScrollCollapseContext.Provider value={valueMemoized}>
      {children}
    </ScrollCollapseContext.Provider>
  );
}

ScrollCollapseProvider.displayName = 'ScrollCollapseProvider';
